import React, { useState, useEffect } from "react";
import * as style from "./CreateEditRoleFeatureSidePanel.module.scss";
import {
  Divider,
  Dropdown,
  DropdownOption,
  DropdownType,
} from "cprime-foundational-components";
import Typography from "../Common/CprimeText";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCurrentRoleId,
  featureRelationsCreateEditPayload,
  updateFeatureRelationStatus,
} from "../../store/slices/CreateRoleSlice";
import { getRoleSetupFeatureDropdownData } from "../../services/RequestAccess/RequestAccess";
import { useParams } from "react-router-dom";

interface CreateEditRoleFeatureSidePanelProps {
  isEdit: boolean;
  selectedRow?: any;
}

const CreateEditRoleFeatureSidePanel: React.FC<
  CreateEditRoleFeatureSidePanelProps
> = ({ isEdit, selectedRow }) => {
  const scssClass = style as any;
  const dispatch = useDispatch();
  const currentRoleId = useSelector(selectCurrentRoleId);

  const [featureOptions, setFeatureOptions] = useState<DropdownOption[]>([]);
  const [featureSelectedOption, setFeatureSelectedOption] = useState<
    DropdownOption[]
  >([]);
  const [permissionsOptions, setPermissionsOptions] = useState<
    DropdownOption[]
  >([]);
  const [permissionsSelectedOptions, setPermissionsSelectedOptions] = useState<
    DropdownOption[]
  >([]);
  const [associatedRoleOptions, setAssociatedRoleOptions] = useState<
    DropdownOption[]
  >([]);
  const [associatedRoleSelectedOptions, setAssociatedRoleSelectedOptions] =
    useState<DropdownOption[]>([]);
  const [applicationId, setApplicationId] = useState<any>();
  const [featureData, setFeatureData] = useState<any>([]);
  const [oldFeatureId, setOldFeatureId] = useState<number | null>(null);

  const { id } = useParams();

  useEffect(() => {
    setApplicationId(id?.split("-")[1]);
  }, [id]);

  const handleFeatureChange = async (selectedFeature: DropdownOption[]) => {
    try {
      const selectedFeatureId = selectedFeature[0]?.id;
      setFeatureSelectedOption(selectedFeature);
      const selectedFeatureVal: any = featureData?.feature?.find(
        (feature: any) => feature?.featureid === Number(selectedFeatureId),
      );
      const permissionOptions = selectedFeatureVal?.permissions?.map(
        (item: any) => ({
          id: item?.id,
          label: item?.permissionname,
          value: item?.id?.toString(),
        }),
      );
      setPermissionsOptions(permissionOptions);
      setPermissionsSelectedOptions(
        permissionOptions?.filter(
          (item: DropdownOption) => item?.label?.toLowerCase() === "view",
        ),
      );
    } catch (error) {
      // console.error("Error fetching features:", error);
    }
  };

  const handlePermissionChange = (selectedPermission: DropdownOption[]) => {
    if (selectedPermission?.length > permissionsOptions?.length) {
      setPermissionsSelectedOptions([]);
    } else if (
      selectedPermission?.length >= 1 &&
      selectedPermission?.some((item) => item.label === "All")
    ) {
      setPermissionsSelectedOptions(permissionsOptions);
    } else {
      setPermissionsSelectedOptions(
        selectedPermission?.filter((item) => item?.label !== "All"),
      );
    }
  };

  const handleAssociatedRoleChange = (selectedRole: DropdownOption[]) => {
    if (selectedRole?.length > associatedRoleOptions?.length) {
      setAssociatedRoleSelectedOptions([]);
    } else if (
      selectedRole?.length >= 1 &&
      selectedRole?.some((item) => item.label === "All")
    ) {
      setAssociatedRoleSelectedOptions(associatedRoleOptions);
    } else {
      // Filter by ID to prevent duplicate label issues
      const uniqueSelected = selectedRole?.filter(
        (item, index, self) =>
          index === self.findIndex((t) => t.id === item.id),
      );
      setAssociatedRoleSelectedOptions(
        uniqueSelected?.filter((item) => item.label !== "All"),
      );
    }
  };

  useEffect(() => {
    const fetchFeatures = async () => {
      if (applicationId) {
        try {
          dispatch(updateFeatureRelationStatus("idle"));
          if (!isEdit) {
            const currentSelections: any = {
              roleId: currentRoleId,
              featureId: "",
              permissionIds: [],
              associatedGroup: [],
            };
            dispatch(featureRelationsCreateEditPayload(currentSelections));
          }
          const response = await getRoleSetupFeatureDropdownData(applicationId);

          const featuresData: any = response?.data ?? [];
          setFeatureData(featuresData);
          if (featuresData) {
            const options = featuresData?.feature?.map((item: any) => ({
              id: item?.featureid,
              label: item?.featurename,
              value: item?.featureid?.toString(),
            }));
            setFeatureOptions(options);

            const associatedGroupsArray = featuresData?.associated_roles;
            if (associatedGroupsArray) {
              const rolesArray = associatedGroupsArray?.map((item: any) => ({
                id: item?.id,
                label: item?.label,
                group: item?.group,
                value: item?.id?.toString(),
                azure_group_name: item?.azure_group_name,
              }));
              setAssociatedRoleOptions(rolesArray);
              setAssociatedRoleSelectedOptions([]);
            }

            if (isEdit) {
              const selectedFeature: any = featuresData?.feature?.find(
                (feature: any) =>
                  feature?.featureid === Number(selectedRow?.feature_id),
              );
              setOldFeatureId(selectedRow?.feature_id);
              const selectedFeatureData = options.filter(
                (opt: any) => opt?.id === selectedRow?.feature_id,
              );
              setFeatureSelectedOption(selectedFeatureData);
              const permissionOptions = selectedFeature?.permissions?.map(
                (item: any) => ({
                  id: item?.id,
                  label: item?.permissionname,
                  value: item?.id?.toString(),
                }),
              );
              setPermissionsOptions(permissionOptions);
              const selectedPermissions = await permissionOptions?.filter(
                (permission: { label: string }) =>
                  selectedRow?.permissions
                    ?.split(", ")
                    ?.includes(permission?.label),
              );
              setPermissionsSelectedOptions(selectedPermissions);
              const associatedGroupsArray =
                featuresData?.associated_roles || [];

              const rolesArray = associatedGroupsArray?.map((item: any) => ({
                id: item?.id,
                label: item?.label,
                group: item?.group,
                value: item?.id?.toString(),
                azure_group_name: item?.azure_group_name,
              }));
              setAssociatedRoleOptions(rolesArray);
              const selectedAssociatedRoles = associatedGroupsArray?.filter(
                (role: { azure_group_name: string }) =>
                  selectedRow?.associated_groups?.includes(
                    role?.azure_group_name,
                  ),
              );
              setAssociatedRoleSelectedOptions(selectedAssociatedRoles);
            }
          } else {
            throw new Error(response?.error);
          }
        } catch (error) {
          // console.error("Error fetching features:", error);
        }
      }
    };

    fetchFeatures();
  }, [applicationId]);

  useEffect(() => {
    if (featureSelectedOption && featureSelectedOption?.length > 0) {
      const currentSelections: any = {
        roleId: currentRoleId,
        featureId: featureSelectedOption?.map((item) => item.id)[0],
        permissionIds: permissionsSelectedOptions?.map((item) => {
          return {
            id: item?.id?.toString(),
            permissionName: item?.label?.toLowerCase(),
          };
        }),
        associatedGroup: associatedRoleSelectedOptions?.map((item) => item?.id),
      };
      if (isEdit) {
        currentSelections.oldFeatureId = oldFeatureId;
      }
      dispatch(featureRelationsCreateEditPayload(currentSelections));
    }
  }, [
    featureSelectedOption,
    permissionsSelectedOptions,
    associatedRoleSelectedOptions,
  ]);

  return (
    <div className={scssClass.panelContent}>
      <Divider
        variant={"fullWidth"}
        sx={{
          marginBottom: "15px",
          width: "100%",
          marginLeft: "0px !important",
          backgroundColor: "#f8f8f9 !important",
        }}
      />
      <Typography className={scssClass.textDescription}>
        Feature Details
      </Typography>

      <div className={scssClass.fieldsContent}>
        <div>
          <Typography className={scssClass.label}>Feature</Typography>
          <Dropdown
            placeholder="Select"
            fullWidth
            optionsTextLimit={200}
            style={{ width: "100%" }}
            multiple={false}
            options={featureOptions}
            selectedOptions={featureSelectedOption}
            onChange={handleFeatureChange}
            error={featureSelectedOption?.length === 0}
            errorMessage={""}
          />
        </div>

        <div>
          <Typography className={scssClass.label}>Permissions</Typography>
          <Dropdown
            placeholder="Select"
            fullWidth
            optionsTextLimit={200}
            style={{ width: "100%" }}
            options={permissionsOptions}
            selectedOptions={permissionsSelectedOptions ?? []}
            onChange={handlePermissionChange}
            type={DropdownType.MultiSelect}
            error={
              permissionsSelectedOptions?.length === 0 ||
              !permissionsSelectedOptions?.some((item) => item.label === "View")
            }
            errorMessage={
              permissionsSelectedOptions?.length === 0
                ? ""
                : "View permission is required"
            }
            selectAllText={"All"}
          />
        </div>

        <div>
          <Typography className={scssClass.label}>Associated Roles</Typography>
          <Dropdown
            placeholder="Select"
            fullWidth
            optionsTextLimit={200}
            style={{ width: "100%" }}
            options={associatedRoleOptions}
            selectedOptions={associatedRoleSelectedOptions}
            onChange={handleAssociatedRoleChange}
            type={DropdownType.FilterMultiSelect}
            selectAllText={"All"}
            isOptionEqualToValue={(option: any, value: any) =>
              option?.id === value?.id
            }
            filterOptions={(options: any, state: any) => {
              const inputValue = state?.inputValue?.toLowerCase();
              if (!inputValue) return options;
              return options?.filter(
                (option: any) =>
                  option?.label?.toLowerCase()?.includes(inputValue) ||
                  option?.group?.toLowerCase()?.includes(inputValue),
              );
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default CreateEditRoleFeatureSidePanel;
